import React from 'react';

interface Forecast {
  productId: string;
  productName: string;
  historicalDailyAverage: number;
  trend: 'up' | 'stable' | 'down';
  recommendedProduction: number;
  confidence: number;
}

interface ProductionForecastProps {
  forecasts: Forecast[];
}

export function ProductionForecast({ forecasts }: ProductionForecastProps) {
  if (!forecasts || forecasts.length === 0) {
    return (
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <p className="text-blue-700">Loading forecast data...</p>
      </div>
    );
  }

  const topProducts = forecasts.slice(0, 10);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Today's Production Forecast</h2>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-3 font-semibold">Product</th>
              <th className="text-center p-3 font-semibold">Avg Daily</th>
              <th className="text-center p-3 font-semibold">Trend</th>
              <th className="text-center p-3 font-semibold">Recommended</th>
              <th className="text-center p-3 font-semibold">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {topProducts.map((forecast) => (
              <tr key={forecast.productId} className="border-b hover:bg-gray-50">
                <td className="p-3">{forecast.productName}</td>
                <td className="text-center p-3 text-gray-600">{forecast.historicalDailyAverage}</td>
                <td className="text-center p-3">
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      forecast.trend === 'up'
                        ? 'bg-green-100 text-green-800'
                        : forecast.trend === 'down'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {forecast.trend === 'up' ? '📈' : forecast.trend === 'down' ? '📉' : '➡️'}
                  </span>
                </td>
                <td className="text-center p-3 font-bold text-amber-600">{forecast.recommendedProduction}</td>
                <td className="text-center p-3">
                  <div className="flex items-center justify-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${forecast.confidence}%` }}
                      />
                    </div>
                    <span className="ml-2 text-xs text-gray-600">{forecast.confidence}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        Forecast based on 30-day sales history. Recommended includes 10% safety buffer.
      </div>
    </div>
  );
}
